import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAppointments, useTransitionAppointment } from '@/hooks/useScheduling';
import { AppointmentStatusBadge } from '@/components/scheduling/AppointmentStatusBadge';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { SCHEDULING_PERMISSIONS, hasPermission } from '@/lib/schedulingPermissions';
import type { ApiComponents } from '@jinzhuan/api-client';
import type { AppointmentStatus } from '@/hooks/useScheduling';

type AppointmentDto = ApiComponents['schemas']['AppointmentDto'];

const SERVICE_TYPE_LABELS: Record<string, string> = {
  consultation: '咨询',
  infusion: '输液',
  followup: '随访',
  assessment: '评估',
  other: '其他',
};

export default function AppointmentListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { appointments, loading, error, fetchAppointments } = useAppointments();
  const { confirmAppointment, cancelAppointment, checkInAppointment, loading: actionLoading } = useTransitionAppointment();

  const [filterStatus, setFilterStatus] = useState<AppointmentStatus | ''>('');
  const [filterDate, setFilterDate] = useState('');

  // Derive permission flags after all hooks are called
  const canRead = hasPermission(permissions, SCHEDULING_PERMISSIONS.APPOINTMENT_READ);
  const canCreate = hasPermission(permissions, SCHEDULING_PERMISSIONS.APPOINTMENT_CREATE);
  const canUpdate = hasPermission(permissions, SCHEDULING_PERMISSIONS.APPOINTMENT_UPDATE);
  const canCancel = hasPermission(permissions, SCHEDULING_PERMISSIONS.APPOINTMENT_CANCEL);

  useEffect(() => {
    // Gate API request: skip fetch when the user lacks read permission
    if (!canRead) return;
    void fetchAppointments({
      status: (filterStatus || undefined) as AppointmentStatus | undefined,
      date: filterDate || undefined,
    });
  }, [fetchAppointments, filterStatus, filterDate, canRead]);

  // Permission early return — after all hooks
  if (!canRead) {
    return <AccessDenied resource="预约列表" />;
  }

  async function handleConfirm(appt: AppointmentDto) {
    const result = await confirmAppointment(appt.id, { expectedVersion: appt.version });
    if (result) {
      void fetchAppointments({ status: (filterStatus || undefined) as AppointmentStatus | undefined, date: filterDate || undefined });
    }
  }

  async function handleCancel(appt: AppointmentDto) {
    if (!confirm('确认取消该预约？')) return;
    const result = await cancelAppointment(appt.id, { expectedVersion: appt.version });
    if (result) {
      void fetchAppointments({ status: (filterStatus || undefined) as AppointmentStatus | undefined, date: filterDate || undefined });
    }
  }

  async function handleCheckIn(appt: AppointmentDto) {
    const result = await checkInAppointment(appt.id, { expectedVersion: appt.version });
    if (result) {
      void fetchAppointments({ status: (filterStatus || undefined) as AppointmentStatus | undefined, date: filterDate || undefined });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">预约列表</h1>
        {canCreate && (
          <button
            onClick={() => navigate('/scheduling/appointments/create')}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            新建预约
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">状态</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as AppointmentStatus | '')}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">全部</option>
            <option value="pending">待确认</option>
            <option value="confirmed">已确认</option>
            <option value="checked_in">已到店</option>
            <option value="completed">已完成</option>
            <option value="cancelled">已取消</option>
            <option value="no_show">未到店</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">日期</label>
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          加载失败：{error.message}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">加载中...</div>
      ) : (
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">客户 ID</th>
                <th className="px-4 py-3 text-left font-medium">服务类型</th>
                <th className="px-4 py-3 text-left font-medium">开始时间</th>
                <th className="px-4 py-3 text-left font-medium">负责员工</th>
                <th className="px-4 py-3 text-left font-medium">状态</th>
                <th className="px-4 py-3 text-left font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {appointments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    暂无预约记录
                  </td>
                </tr>
              ) : (
                appointments.map((appt) => (
                  <tr key={appt.id} className="border-b hover:bg-muted/25">
                    <td className="px-4 py-3 font-mono text-xs">{appt.customerId}</td>
                    <td className="px-4 py-3">
                      {SERVICE_TYPE_LABELS[appt.serviceType] ?? appt.serviceType}
                    </td>
                    <td className="px-4 py-3">
                      {new Date(appt.startAt).toLocaleString('zh-CN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{appt.assignedStaffId}</td>
                    <td className="px-4 py-3">
                      <AppointmentStatusBadge status={appt.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/scheduling/appointments/${appt.id}`)}
                          className="text-xs text-primary hover:underline"
                        >
                          详情
                        </button>
                        {canUpdate && appt.status === 'pending' && (
                          <button
                            onClick={() => void handleConfirm(appt)}
                            disabled={actionLoading}
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                          >
                            确认
                          </button>
                        )}
                        {canUpdate && appt.status === 'confirmed' && (
                          <button
                            onClick={() => void handleCheckIn(appt)}
                            disabled={actionLoading}
                            className="text-xs text-green-600 hover:underline disabled:opacity-50"
                          >
                            到店
                          </button>
                        )}
                        {canCancel && (appt.status === 'pending' || appt.status === 'confirmed') && (
                          <button
                            onClick={() => void handleCancel(appt)}
                            disabled={actionLoading}
                            className="text-xs text-destructive hover:underline disabled:opacity-50"
                          >
                            取消
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
